// Y-axis display range for a correlation-finder chart, ported from the partial block
// (getDisplayRange). The exporters ask for it so a static image keeps the axis the researcher is
// looking at; the rendered chart answers first, and this maths is the fallback for a chart that has
// not drawn yet.
//
// A correlation is a pair, so the walk is batches x answer variants rather than the single answer
// variant list the rest of the families walk.

import { getErrData } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/errData'
import { calcIgnoreAmount, calcIgnorePercents } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'

const NON_RANGE_CHART_MODES = ['none']

// A stacked column is a sum of its pair, so its axis is not the tallest point and this maths does
// not describe it.
const STACKED_CHART_MODES = ['stacked', 'bstacked']

// which column of an answer the chart plots, and the range therefore measures
const plottedColumn = (axisType, answerType) => ('respondents' === axisType ? answerType || 'amount' : 'percents')

// Whether the chart on screen has an axis worth describing at all. Asked before the rendered chart
// is, so a chart that has one is not consulted about a range it could not have.
export const hasDisplayRange = (chartMode, globalChartMode) =>
  !NON_RANGE_CHART_MODES.includes(chartMode) &&
  !NON_RANGE_CHART_MODES.includes(globalChartMode) &&
  !STACKED_CHART_MODES.includes(chartMode)

export const calcDisplayRange = ({
  chartMode,
  zoomLevel,
  globalChartMode,
  format = {},
  answer = {},
  batches = [],
  axisType,
  answerType = 'amount',
  ignoreAmount: ignoreAmountFlag,
  ignorePercents: ignorePercentsFlag,
  confidenceLevels = []
} = {}) => {
  if (!hasDisplayRange(chartMode, globalChartMode)) return null

  if (!['min', 'max'].includes(zoomLevel)) return zoomLevel ?? null

  const percentsMode = 'percents' === axisType
  const mode = plottedColumn(axisType, answerType)

  const ignoreAmount = calcIgnoreAmount(format, ignoreAmountFlag)
  const ignorePercents = calcIgnorePercents(format, ignorePercentsFlag)

  const max = Math.ceil(
    (batches || []).reduce(
      (acc, batch) =>
        (batch?.answerVariants || []).reduce((batchAcc, av) => {
          if (!av) return batchAcc

          const as = (answer[`b${batch.position}`] || {})[`a${av.position}`] || { [answerType]: 0, percents: 0 }

          const { maxErrValue } = getErrData(as, mode, {
            ignoreAmount,
            ignorePercents,
            rType: 'cases',
            answerType,
            confidenceLevels
          })

          return Math.max(batchAcc, (parseFloat(as[mode]) || 0) + (maxErrValue || 0))
        }, acc),
      0
    )
  )

  if ('max' === zoomLevel) return [0, Math.max(max, (percentsMode && 100) || answer.total || 0)]

  return [0, max]
}
